import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { title: "", location: "", type: "Full-time", experience: "", description: "" };
const TYPES = ["Full-time", "Contract", "Internship", "Remote"];

const inputCls =
  "w-full bg-zinc-900 border border-zinc-800 px-4 py-3 text-white placeholder:text-zinc-600 text-sm focus:outline-none focus:border-primary transition-colors";

export default function JobManager() {
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use((config) => {
      const token = localStorage.getItem("hr99_token");
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    instance.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem("hr99_token");
          navigate("/admin/login");
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/jobs");
      setJobs(data);
    } catch {
      toast.error("Could not load jobs");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/jobs/${editing}`, form);
        setJobs((list) => list.map((j) => (j.id === editing ? data : j)));
        toast.success("Opening updated");
      } else {
        const { data } = await api.post("/jobs", form);
        setJobs((list) => [data, ...list]);
        toast.success("Opening published");
      }
      setForm(empty);
      setEditing(null);
      setShowForm(false);
    } catch {
      toast.error("Could not save opening");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (j) => {
    setForm({ title: j.title, location: j.location, type: j.type, experience: j.experience || "", description: j.description || "" });
    setEditing(j.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    try {
      await api.delete(`/jobs/${id}`);
      setJobs((list) => list.filter((j) => j.id !== id));
      toast.success("Opening removed");
    } catch {
      toast.error("Could not remove opening");
    }
  };

  return (
    <div data-testid="job-manager">
      <div className="flex items-center justify-between mb-8">
        <p className="text-sm text-zinc-400">
          These openings appear on the website's <span className="text-white">contact section</span>. Changes go live instantly.
        </p>
        <button
          onClick={() => { setForm(empty); setEditing(null); setShowForm(!showForm); }}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-orange-400 transition-colors"
          data-testid="job-add-toggle"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showForm ? "Cancel" : "Add Opening"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-zinc-950/60 border border-white/10 p-6 md:p-8 mb-10 space-y-4" data-testid="job-form">
          <p className="font-heading text-xl text-white">{editing ? "Edit opening" : "New opening"}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input required value={form.title} onChange={set("title")} placeholder="Job title *" className={inputCls} data-testid="job-title-input" />
            <input required value={form.location} onChange={set("location")} placeholder="Location *" className={inputCls} data-testid="job-location-input" />
            <select value={form.type} onChange={set("type")} className={`${inputCls} appearance-none`} data-testid="job-type-select">
              {TYPES.map((t) => (
                <option key={t} value={t} className="bg-zinc-900">{t}</option>
              ))}
            </select>
            <input value={form.experience} onChange={set("experience")} placeholder="Experience (e.g. 1–3 years, Fresher)" className={inputCls} data-testid="job-experience-input" />
          </div>
          <textarea rows={3} value={form.description} onChange={set("description")} placeholder="Short description of the role..." className={`${inputCls} resize-none`} data-testid="job-description-input" />
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold hover:bg-orange-400 transition-colors disabled:opacity-60" data-testid="job-save-button">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing ? "Save Changes" : "Publish Opening"}
          </button>
        </form>
      )}

      <p className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-4">
        Live openings ({jobs.length})
      </p>
      <div className="space-y-3" data-testid="admin-job-list">
        {jobs.map((j) => (
          <div key={j.id} className="bg-zinc-900 border border-zinc-800 px-6 py-5 flex flex-wrap items-center justify-between gap-4" data-testid={`admin-job-${j.id}`}>
            <div>
              <p className="font-heading text-lg font-medium text-white">{j.title}</p>
              <p className="mt-1 flex items-center gap-2 text-sm text-zinc-500">
                <MapPin className="w-3.5 h-3.5" /> {j.location} · {j.type}{j.experience ? ` · ${j.experience}` : ""}
              </p>
              {j.description && (
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{j.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => startEdit(j)} className="px-3 py-2 text-xs font-semibold border border-white/15 text-zinc-300 hover:border-primary hover:text-primary transition-colors inline-flex items-center gap-1.5" data-testid={`job-edit-${j.id}`}>
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
              <button onClick={() => remove(j.id)} className="px-3 py-2 text-xs font-semibold border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors inline-flex items-center gap-1.5" data-testid={`job-delete-${j.id}`}>
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          </div>
        ))}
        {jobs.length === 0 && (
          <p className="text-sm text-zinc-600" data-testid="job-empty">No live openings — add the first one.</p>
        )}
      </div>
    </div>
  );
}
